import { AlertCircle, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { useCustomsActions } from './stores/useCustomsStore'
import {
  useImportFileActions,
  useImportFileState
} from './stores/useImportFileStore'
import { useSearchActions } from './stores/useSearchStore'

import FileDropZone from '@/components/form/FileDropZone'
import { useUIStore } from '@/stores/useUIStore'
import { getPasteKeyHint } from '@/utils/platform'

const ImportExcel: React.FC = () => {
  const { showError, showSuccess } = useUIStore()
  const { file, loading, errors } = useImportFileState()
  const { loadFile, clearFile, doUpload, clearErrors } = useImportFileActions()
  const { changeTabLabel } = useCustomsActions()
  const { fetchMasterBills, changeMasterBillId } = useSearchActions()

  // 本地错误状态管理
  const [localError, setLocalError] = useState<string>('')

  // 获取适当的粘贴快捷键提示
  const pasteKeyHint = getPasteKeyHint()

  const handleImport = async () => {
    setLocalError('') // 清除本地错误
    clearErrors() // 清除store中的errors
    const {success, message, data} = await doUpload()
    if (success && data) {
      showSuccess(message)
      const {success} = await fetchMasterBills()
      if (success && data.masterBillId) {
        changeMasterBillId(data.masterBillId)
        changeTabLabel('unpacking')
      }
    } else {
      setLocalError(message)
    }
  }

  const handleFileSelect = (selectedFile: File) => {
    setLocalError('') // 清除本地错误
    clearErrors() // 清除store中的errors
    loadFile(selectedFile)
  }

  const dismissLocalError = () => {
    setLocalError('')
  }

  const dismissStoreErrors = () => {
    clearErrors()
  }

  // 页面级别的粘贴事件监听
  useEffect(() => {
    const handlePagePaste = (e: ClipboardEvent) => {
      // 如果已经有文件或正在加载，则不处理
      if (file || loading) return

      const items = e.clipboardData?.items
      if (!items) return

      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.kind === 'file') {
          const pastedFile = item.getAsFile()
          if (pastedFile) {
            // 验证文件类型和大小
            const allowedTypes = ['.xlsx', '.xls']
            const isValidType = allowedTypes.some((type) =>
              pastedFile.name.toLowerCase().endsWith(type.toLowerCase())
            )
            const maxSize = 50 * 1024 * 1024 // 50MB

            if (!isValidType) {
              setLocalError('请粘贴有效的Excel文件 (.xlsx, .xls)')
              return
            }

            if (pastedFile.size > maxSize) {
              setLocalError('文件大小不能超过 50MB')
              return
            }

            handleFileSelect(pastedFile)
            e.preventDefault()
          }
          break
        }
      }
    }

    document.addEventListener('paste', handlePagePaste)
    return () => {
      document.removeEventListener('paste', handlePagePaste)
    }
  }, [file, loading, loadFile, showError])

  return (
    <div className="mx-auto space-y-6">
      {/* 动态高度的错误消息区域 */}
      <div className="space-y-3">
        {/* 本地错误（文件验证等） */}
        {localError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">文件验证错误</p>
                <p className="text-sm text-red-700">{localError}</p>
              </div>
            </div>
            <button
              onClick={dismissLocalError}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Store中的详细错误（服务器返回） */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 mb-2">数据验证错误</p>
                  <div className="space-y-1">
                    {errors.map((error, index) => (
                      <p key={index} className="text-sm text-red-700 flex items-start space-x-2">
                        <span className="text-red-500 mt-1">•</span>
                        <span>{error}</span>
                      </p>
                    ))}
                  </div>
                  {errors.length > 3 && (
                    <p className="text-xs text-red-600 mt-2">
                      共发现 {errors.length} 个错误，请修复后重新导入
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={dismissStoreErrors}
                className="text-red-400 hover:text-red-600 transition-colors ml-3"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 主要内容区域 - 左右布局 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左侧：导入区域 */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Excel文件导入</h3>
              <p className="text-sm text-gray-600 mt-1">
                支持拖拽、点击选择或在页面任意位置按 {pasteKeyHint} 粘贴文件
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* 文件选择区域 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  选择Excel文件
                </label>
                <FileDropZone
                  onFileSelect={handleFileSelect}
                  accept=".xlsx,.xls"
                  maxSize={50 * 1024 * 1024}
                  disabled={loading}
                  className="w-full"
                />
              </div>

              {/* 已选文件信息 */}
              {file && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        文件已选择
                      </p>
                      <p className="text-sm text-gray-600 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        大小: {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    clearFile() // 这已经会清除store中的errors
                    setLocalError('') // 清除本地错误
                  }}
                  disabled={!file || loading}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  取消选择
                </button>
                <button
                  onClick={handleImport}
                  disabled={!file || loading}
                  className="w-full sm:w-auto px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>导入中...</span>
                    </div>
                  ) : (
                    '开始导入'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧：导入说明 */}
        <div className="lg:col-span-1">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 h-fit sticky top-6">
            <h4 className="text-base font-medium text-blue-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              导入说明
            </h4>
            
            <div className="space-y-4">
              {/* 文件要求 */}
              <div>
                <h5 className="text-sm font-medium text-blue-900 mb-2">文件要求</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>支持 .xlsx 和 .xls 格式</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>最大文件大小: 50MB</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>确保数据格式正确</span>
                  </li>
                </ul>
              </div>

              {/* 上传方式 */}
              <div>
                <h5 className="text-sm font-medium text-blue-900 mb-2">上传方式</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>拖拽文件到上传区域</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>点击上传区域选择文件</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>页面任意位置 {pasteKeyHint} 粘贴</span>
                  </li>
                </ul>
              </div>

              {/* 导入内容 */}
              <div>
                <h5 className="text-sm font-medium text-blue-900 mb-2">导入内容</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>主提单基本信息</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>分提单基本信息</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5 text-xs">•</span>
                    <span>自动数据完整性校验</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 提示信息 */}
            <div className="mt-4 p-3 bg-blue-100 rounded-md">
              <p className="text-xs text-blue-700">
                💡 导入过程中如遇到错误，请根据错误提示修正数据后重新导入
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImportExcel
